import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchMe } from "@/lib/api";

const AppContext = createContext(null);

export const LANGS = [
  { code: "en", label: "EN" },
  { code: "nl", label: "NL" },
  { code: "bg", label: "BG" },
  { code: "uk", label: "UK" },
];

const SUPPORTED = LANGS.map((l) => l.code);

// First visit: detect from the browser. Any saved manual choice always wins.
function initialLang() {
  const stored = localStorage.getItem("rbl_lang");
  if (stored && SUPPORTED.includes(stored)) return stored;
  const navs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ""];
  for (const n of navs) {
    const code = String(n).slice(0, 2).toLowerCase();
    if (SUPPORTED.includes(code)) {
      localStorage.setItem("rbl_lang", code);
      return code;
    }
  }
  return "en";
}

export function AppProvider({ children }) {
  const [lang, setLangState] = useState(initialLang);
  const [token, setToken] = useState(() => localStorage.getItem("rbl_token") || null);
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const setLang = useCallback((code) => {
    localStorage.setItem("rbl_lang", code);
    setLangState(code);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("rbl_token");
    setToken(null);
    setUser(null);
  }, []);

  const applyAuth = useCallback((accessToken, userObj) => {
    localStorage.setItem("rbl_token", accessToken);
    setToken(accessToken);
    setUser(userObj);
  }, []);

  useEffect(() => {
    let active = true;
    if (!token) {
      setAuthReady(true);
      return;
    }
    fetchMe()
      .then((u) => active && setUser(u))
      .catch(() => active && logout())
      .finally(() => active && setAuthReady(true));
    return () => {
      active = false;
    };
  }, [token, logout]);

  return (
    <AppContext.Provider value={{ lang, setLang, token, user, authReady, applyAuth, logout }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
